// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { UserPlus, Pencil, Trash2, Download, Plus, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  useAdminComplianceSummary, useAdminPartnerStats, useAdminComplianceByEvent,
  useAdminRoomNights, useAdminOutreach, useAdminRateExpiry, useAdminRevenue,
} from './useAdminReportsData';

function exportCsv(filename: string, headers: string[], rows: string[][]) {
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const AdminHotelPartnersPage: React.FC = () => {
  const { user } = useAuth();
  const qc = useQueryClient();

  const [showAddPartner, setShowAddPartner] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [assignTarget, setAssignTarget] = useState<string | null>(null); // partner user_id
  const [assignEventId, setAssignEventId] = useState('');
  const [assignBookingUrl, setAssignBookingUrl] = useState('');
  const [assignExpiry, setAssignExpiry] = useState('');
  const [editAssignment, setEditAssignment] = useState<any | null>(null);
  const [editBookingUrl, setEditBookingUrl] = useState('');
  const [editExpiry, setEditExpiry] = useState('');
  const [activeTab, setActiveTab] = useState('partners');

  // ── Partners list: user_roles role='hotel_partner' + profiles ──
  const { data: partners, isLoading: partnersLoading } = useQuery({
    queryKey: ['admin-hotel-partners'],
    queryFn: async () => {
      const { data: roles, error } = await (supabase as any)
        .from('user_roles')
        .select('user_id')
        .eq('role', 'hotel_partner');
      if (error) throw error;
      const userIds = (roles || []).map((r: any) => r.user_id);
      if (userIds.length === 0) return [];
      const { data: profs, error: e2 } = await (supabase as any)
        .from('profiles')
        .select('user_id, first_name, last_name, name, email, created_at')
        .in('user_id', userIds);
      if (e2) throw e2;
      return profs || [];
    },
  });

  // ── Assignments per partner ──
  const { data: assignments } = useQuery({
    queryKey: ['admin-hotel-assignments'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('id, event_id, hotel_partner_user_id, booking_url, group_rate_expiry, events!inner(name, start_date)');
      if (error) throw error;
      return data || [];
    },
  });

  const assignmentsByPartner = useMemo(() => {
    const map = new Map<string, any[]>();
    (assignments || []).forEach((a: any) => {
      if (!map.has(a.hotel_partner_user_id)) map.set(a.hotel_partner_user_id, []);
      map.get(a.hotel_partner_user_id)!.push(a);
    });
    return map;
  }, [assignments]);

  // ── All events (for assign modal) ──
  const { data: allEvents } = useQuery({
    queryKey: ['admin-hotel-all-events'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('events')
        .select('id, name, start_date')
        .order('start_date', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // ── Search users for adding a partner ──
  const { data: searchResults } = useQuery({
    queryKey: ['admin-hotel-partner-search', partnerSearch],
    enabled: partnerSearch.length >= 2,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('profiles')
        .select('user_id, first_name, last_name, name, email')
        .or(`name.ilike.%${partnerSearch}%,email.ilike.%${partnerSearch}%,first_name.ilike.%${partnerSearch}%,last_name.ilike.%${partnerSearch}%`)
        .limit(10);
      if (error) throw error;
      return data || [];
    },
  });

  // ── Grant hotel_partner role (user_roles only — never profiles.role) ──
  const grantRole = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await (supabase as any)
        .from('user_roles')
        .insert({ user_id: userId, role: 'hotel_partner' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-hotel-partners'] });
      toast.success('Hotel partner role granted');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to grant role'),
  });

  // ── Remove hotel_partner role ──
  const removeRole = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await (supabase as any)
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role', 'hotel_partner');
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-hotel-partners'] });
      qc.invalidateQueries({ queryKey: ['admin-hotel-assignments'] });
      toast.success('Hotel partner role removed');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to remove role'),
  });

  // ── Assign event to partner ──
  const assignEvent = useMutation({
    mutationFn: async ({ partnerUserId, eventId, bookingUrl, expiry }: { partnerUserId: string; eventId: string; bookingUrl: string; expiry: string }) => {
      const { error } = await (supabase as any)
        .from('hotel_event_assignments')
        .insert({
          event_id: eventId,
          hotel_partner_user_id: partnerUserId,
          booking_url: bookingUrl || null,
          group_rate_expiry: expiry || null,
          assigned_by: user?.id,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-hotel-assignments'] });
      toast.success('Event assigned');
      setAssignTarget(null);
      setAssignEventId('');
      setAssignBookingUrl('');
      setAssignExpiry('');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to assign event'),
  });

  // ── Unassign event ──
  const unassignEvent = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await (supabase as any)
        .from('hotel_event_assignments')
        .delete()
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-hotel-assignments'] });
      toast.success('Assignment removed');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to remove assignment'),
  });

  // ── Update assignment (booking_url / group_rate_expiry) ──
  const updateAssignment = useMutation({
    mutationFn: async ({ id, bookingUrl, expiry }: { id: string; bookingUrl: string; expiry: string }) => {
      const { error } = await (supabase as any)
        .from('hotel_event_assignments')
        .update({ booking_url: bookingUrl || null, group_rate_expiry: expiry || null })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-hotel-assignments'] });
      toast.success('Assignment updated');
      setEditAssignment(null);
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update assignment'),
  });

  // Report hooks
  const summary = useAdminComplianceSummary();
  const partnerStats = useAdminPartnerStats();
  const complianceByEvent = useAdminComplianceByEvent();
  const roomNights = useAdminRoomNights();
  const outreachData = useAdminOutreach();
  const rateExpiry = useAdminRateExpiry();
  const revenue = useAdminRevenue();

  const alreadyPartnerIds = new Set((partners || []).map((p: any) => p.user_id));
  const filteredSearch = (searchResults || []).filter((p: any) => !alreadyPartnerIds.has(p.user_id));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Hotel Partners</h1>
        <Button onClick={() => setShowAddPartner(true)}>
          <UserPlus className="h-4 w-4 mr-2" /> Add Partner
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="partners">Partners</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        {/* ── Partners tab ── */}
        <TabsContent value="partners" className="space-y-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              {partnersLoading ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : !partners || partners.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground">No hotel partners yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Assigned Events</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partners.map((p: any) => {
                      const partnerAssignments = assignmentsByPartner.get(p.user_id) || [];
                      return (
                        <TableRow key={p.user_id}>
                          <TableCell className="font-medium">
                            {[p.first_name, p.last_name].filter(Boolean).join(' ') || p.name || '—'}
                          </TableCell>
                          <TableCell className="text-sm">{p.email}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {partnerAssignments.map((a: any) => (
                                <Badge key={a.id} variant="secondary" className="text-xs gap-1">
                                  {a.events?.name || 'Event'}
                                  <button
                                    className="ml-1 hover:text-destructive"
                                    onClick={() => unassignEvent.mutate(a.id)}
                                    title="Remove assignment"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                  <button
                                    className="hover:text-primary"
                                    onClick={() => {
                                      setEditAssignment(a);
                                      setEditBookingUrl(a.booking_url || '');
                                      setEditExpiry(a.group_rate_expiry || '');
                                    }}
                                    title="Edit assignment"
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => setAssignTarget(p.user_id)}
                              >
                                <Plus className="h-3 w-3 mr-1" /> Assign
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {p.created_at ? format(parseISO(p.created_at), 'MMM d, yyyy') : '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                if (window.confirm('Remove hotel partner role? Their event assignments will remain but they will lose portal access.')) {
                                  removeRole.mutate(p.user_id);
                                }
                              }}
                            >
                              <Trash2 className="h-3 w-3 mr-1" /> Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Reports tab ── */}
        <TabsContent value="reports" className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Events', value: summary.data?.total_events },
              { label: 'Total Teams', value: summary.data?.total_teams },
              { label: 'Confirmed', value: summary.data?.confirmed },
              { label: 'Unbooked', value: summary.data?.unbooked },
              { label: 'Avg Compliance', value: summary.data ? `${summary.data.avg_compliance}%` : undefined },
              { label: 'Attrition Risks', value: summary.data?.attrition_risks },
            ].map((s) => (
              <Card key={s.label}>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  {summary.isLoading ? (
                    <Skeleton className="h-7 w-12 mx-auto mt-1" />
                  ) : (
                    <p className="text-xl font-bold">{s.value ?? 0}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Partner stats */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Partner Performance</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const d = partnerStats.data || [];
                  exportCsv('partner-performance.csv',
                    ['Name', 'Email', 'Events', 'Confirmed', 'Unbooked', 'Room Nights', 'Emails Sent'],
                    d.map((p: any) => [
                      [p.first_name, p.last_name].filter(Boolean).join(' '),
                      p.email, p.events, p.confirmed, p.unbooked, p.room_nights, p.emails_sent,
                    ]));
                }}
              >
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Partner</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Confirmed</TableHead>
                    <TableHead>Unbooked</TableHead>
                    <TableHead>Room Nights</TableHead>
                    <TableHead>Emails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(partnerStats.data || []).map((p: any) => (
                    <TableRow key={p.user_id}>
                      <TableCell className="font-medium">
                        {[p.first_name, p.last_name].filter(Boolean).join(' ') || p.email}
                      </TableCell>
                      <TableCell>{p.events}</TableCell>
                      <TableCell>{p.confirmed}</TableCell>
                      <TableCell className="text-red-600">{p.unbooked}</TableCell>
                      <TableCell>{p.room_nights}</TableCell>
                      <TableCell>{p.emails_sent}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Compliance by event */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Compliance by Event</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const d = complianceByEvent.data || [];
                  exportCsv('compliance-by-event.csv',
                    ['Event', 'Date', 'Teams', 'Confirmed', 'Exempt', 'Unbooked', 'Compliance %'],
                    d.map((r: any) => [r.event_name, r.start_date, r.total_teams, r.confirmed_bookings, r.exempt_bookings, r.unbooked_teams, `${r.compliance_pct}%`]));
                }}
              >
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Teams</TableHead>
                    <TableHead>Confirmed</TableHead>
                    <TableHead>Unbooked</TableHead>
                    <TableHead>Compliance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(complianceByEvent.data || []).map((r: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{r.event_name}</TableCell>
                      <TableCell className="text-sm">{r.start_date ? format(parseISO(r.start_date), 'MMM d, yyyy') : '—'}</TableCell>
                      <TableCell>{r.total_teams}</TableCell>
                      <TableCell>{r.confirmed_bookings}</TableCell>
                      <TableCell className="text-red-600">{r.unbooked_teams}</TableCell>
                      <TableCell>
                        <Badge variant={r.compliance_pct >= 80 ? 'default' : 'destructive'}>
                          {r.compliance_pct}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Revenue */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Revenue Summary</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const d = revenue.data || [];
                  exportCsv('revenue-summary.csv',
                    ['Event', 'Hotel', 'Room Nights', 'Gross', 'Commission', 'Rebate'],
                    d.map((r: any) => [r.events?.name, r.hotel_properties?.hotel_name, r.total_room_nights, r.gross_revenue, r.total_commission, r.total_rebate]));
                }}
              >
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Hotel</TableHead>
                    <TableHead>Room Nights</TableHead>
                    <TableHead>Gross</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Rebate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(revenue.data || []).map((r: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{r.events?.name}</TableCell>
                      <TableCell>{r.hotel_properties?.hotel_name}</TableCell>
                      <TableCell>{r.total_room_nights}</TableCell>
                      <TableCell>${Number(r.gross_revenue || 0).toLocaleString()}</TableCell>
                      <TableCell>${Number(r.total_commission || 0).toLocaleString()}</TableCell>
                      <TableCell>${Number(r.total_rebate || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Rate expiry watch */}
          <Card>
            <CardHeader><CardTitle className="text-base">Rate Expiry Watch</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Days Left</TableHead>
                    <TableHead>Unbooked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(rateExpiry.data || []).map((r: any, i: number) => (
                    <TableRow key={i} className={r.days_remaining <= 7 ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                      <TableCell>{r.event_name}</TableCell>
                      <TableCell>{r.group_rate_expiry ? format(parseISO(r.group_rate_expiry), 'MMM d, yyyy') : '—'}</TableCell>
                      <TableCell>
                        <Badge variant={r.days_remaining <= 7 ? 'destructive' : 'secondary'}>{r.days_remaining}d</Badge>
                      </TableCell>
                      <TableCell>{r.unbooked_teams}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Partner dialog */}
      <Dialog open={showAddPartner} onOpenChange={setShowAddPartner}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Hotel Partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              placeholder="Search by name or email..."
              value={partnerSearch}
              onChange={(e) => setPartnerSearch(e.target.value)}
            />
            {partnerSearch.length >= 2 && (
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {filteredSearch.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No users found.</p>
                ) : (
                  filteredSearch.map((p: any) => (
                    <div key={p.user_id} className="flex items-center justify-between border rounded-md px-3 py-2">
                      <div>
                        <p className="text-sm font-medium">
                          {[p.first_name, p.last_name].filter(Boolean).join(' ') || p.name || '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">{p.email}</p>
                      </div>
                      <Button size="sm" onClick={() => { grantRole.mutate(p.user_id); setShowAddPartner(false); setPartnerSearch(''); }}>
                        Grant
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPartner(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign event dialog */}
      <Dialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Event</label>
              <Select value={assignEventId} onValueChange={setAssignEventId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select event..." />
                </SelectTrigger>
                <SelectContent>
                  {(allEvents || []).map((ev: any) => (
                    <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Booking URL</label>
              <Input value={assignBookingUrl} onChange={(e) => setAssignBookingUrl(e.target.value)} placeholder="https://..." />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Group Rate Expiry</label>
              <Input type="date" value={assignExpiry} onChange={(e) => setAssignExpiry(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTarget(null)}>Cancel</Button>
            <Button
              onClick={() => assignTarget && assignEventId && assignEvent.mutate({ partnerUserId: assignTarget, eventId: assignEventId, bookingUrl: assignBookingUrl, expiry: assignExpiry })}
              disabled={!assignEventId || assignEvent.isPending}
            >
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit assignment dialog */}
      <Dialog open={!!editAssignment} onOpenChange={(v) => !v && setEditAssignment(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Assignment — {editAssignment?.events?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Booking URL</label>
              <Input value={editBookingUrl} onChange={(e) => setEditBookingUrl(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Group Rate Expiry</label>
              <Input type="date" value={editExpiry} onChange={(e) => setEditExpiry(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAssignment(null)}>Cancel</Button>
            <Button
              onClick={() => editAssignment && updateAssignment.mutate({ id: editAssignment.id, bookingUrl: editBookingUrl, expiry: editExpiry })}
              disabled={updateAssignment.isPending}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHotelPartnersPage;
